/* Product card image carousel.
   Touch uses native scrolling; mouse uses pointer dragging. */

if (!customElements.get('card-media-carousel')) {
  customElements.define(
    'card-media-carousel',
    class CardMediaCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.viewport = this.querySelector(
          '.card-media-carousel__viewport'
        );
        if (!this.viewport) return;

        this.initialized = true;
        this.progressBar = this.querySelector(
          '.card-media-carousel__progress-bar'
        );
        this.prevButton = this.querySelector(
          '.card-media-carousel__arrow--prev'
        );
        this.nextButton = this.querySelector(
          '.card-media-carousel__arrow--next'
        );

        this.slideCount =
          Number(this.dataset.slideCount) ||
          this.viewport.children.length;

        this.controller = new AbortController();
        const { signal } = this.controller;

        this.behavior = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
          ? 'auto'
          : 'smooth';

        this.drag = null;
        this.touchStart = null;
        this.suppressUntil = 0;

        const blockFollowingClick = () => {
          this.suppressUntil = performance.now() + 700;
        };

        this.viewport.addEventListener(
          'scroll',
          () => {
            if (this.touchStart) {
              blockFollowingClick();
            }
            this.update();
          },
          { passive: true, signal }
        );

                this.prevButton?.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.goTo(this.currentIndex() - 1);
          },
          { signal }
        );

        this.nextButton?.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.goTo(this.currentIndex() + 1);
          },
          { signal }
        );


        this.viewport.addEventListener(
          'touchstart',
          (event) => {
            this.suppressUntil = 0;
            const touch = event.touches[0];
            this.touchStart = touch
              ? { x: touch.clientX, y: touch.clientY }
              : null;
          },
          { passive: true, signal }
        );

        this.viewport.addEventListener(
          'touchmove',
          (event) => {
            if (!this.touchStart) return;
            const touch = event.touches[0];
            if (!touch) return;

            const distanceX = Math.abs(
              touch.clientX - this.touchStart.x
            );
            const distanceY = Math.abs(
              touch.clientY - this.touchStart.y
            );

            if (distanceX > 8 || distanceY > 8) {
              blockFollowingClick();
            }
          },
          { passive: true, signal }
        );

        const finishTouch = () => {
          this.touchStart = null;
        };

        this.viewport.addEventListener(
          'touchend',
          finishTouch,
          { passive: true, signal }
        );

        this.viewport.addEventListener(
          'touchcancel',
          finishTouch,
          { passive: true, signal }
        );

        this.viewport.addEventListener(
          'pointerdown',
          (event) => {
            if (
              event.pointerType !== 'mouse' ||
              event.button !== 0
            ) {
              return;
            }

            this.suppressUntil = 0;
            this.drag = {
              id: event.pointerId,
              x: event.clientX,
              scroll: this.viewport.scrollLeft,
              moved: false
            };
          },
          { signal }
        );

        window.addEventListener(
          'pointermove',
          (event) => {
            if (
              !this.drag ||
              event.pointerId !== this.drag.id
            ) {
              return;
            }

            const delta = event.clientX - this.drag.x;

            if (!this.drag.moved && Math.abs(delta) > 6) {
              this.drag.moved = true;
              this.viewport.classList.add('is-dragging');
              this.viewport.setPointerCapture(event.pointerId);
            }

            if (this.drag.moved) {
              if (event.cancelable) event.preventDefault();

              this.viewport.scrollLeft =
                this.drag.scroll - delta;

              blockFollowingClick();
            }
          },
          { passive: false, signal }
        );

        const finishDrag = (event) => {
          if (
            !this.drag ||
            event.pointerId !== this.drag.id
          ) {
            return;
          }

          const moved = this.drag.moved;
          const pointerId = this.drag.id;
          const index = this.currentIndex();
          this.drag = null;

          if (this.viewport.hasPointerCapture(pointerId)) {
            this.viewport.releasePointerCapture(pointerId);
          }

          this.viewport.classList.remove('is-dragging');

          if (moved) {
            blockFollowingClick();
            this.goTo(index);
          }
        };

        window.addEventListener(
          'pointerup',
          finishDrag,
          { signal }
        );

        window.addEventListener(
          'pointercancel',
          finishDrag,
          { signal }
        );

        this.viewport.addEventListener(
          'lostpointercapture',
          finishDrag,
          { signal }
        );

        this.addEventListener(
          'click',
          (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            if (
              target.closest('.card-media-carousel__link') &&
              event.detail !== 0 &&
              performance.now() < this.suppressUntil
            ) {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.suppressUntil = 0;
            }
          },
          { capture: true, signal }
        );

        this.addEventListener(
          'dragstart',
          (event) => event.preventDefault(),
          { signal }
        );

        this.resizeObserver = new ResizeObserver(() => {
          this.update();
        });

        this.resizeObserver.observe(this.viewport);
        this.update();

        if (
          window.CSS &&
          CSS.supports('selector(:has(div))')
        ) {
          this.classList.add('card-media-carousel--ready');
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.resizeObserver?.disconnect();
        this.viewport?.classList.remove('is-dragging');
        this.classList.remove('card-media-carousel--ready');
        this.drag = null;
        this.touchStart = null;
        this.initialized = false;
      }

      currentIndex() {
        const width = this.viewport.clientWidth;
        if (!width) return 0;

        return Math.min(
          this.slideCount - 1,
          Math.max(
            0,
            Math.round(this.viewport.scrollLeft / width)
          )
        );
      }

      goTo(index) {
        const target = Math.min(
          this.slideCount - 1,
          Math.max(0, index)
        );

        this.viewport.scrollTo({
          left: target * this.viewport.clientWidth,
          behavior: this.behavior
        });
      }

      update() {
        const maximum =
          this.viewport.scrollWidth -
          this.viewport.clientWidth;

        const fraction =
          maximum > 0
            ? Math.min(
                1,
                Math.max(
                  0,
                  this.viewport.scrollLeft / maximum
                )
              )
            : 0;

        if (this.progressBar) {
          this.progressBar.style.transform =
            `translateX(${fraction * (this.slideCount - 1) * 100}%)`;
        }

        const index = this.currentIndex();

        if (this.prevButton) {
          this.prevButton.disabled = index <= 0;
        }

        if (this.nextButton) {
          this.nextButton.disabled =
            index >= this.slideCount - 1;
        }
      }
    }
  );
}
